import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { PageHeader } from "@/components/app/page-header";
import { HelpPanel } from "@/components/app/help-panel";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { getBounceMonitorStats } from "@/lib/bounce-monitor-stats.functions";

export const Route = createFileRoute("/_authenticated/bounce-monitor")({
  component: BounceMonitorPage,
  head: () => ({ meta: [{ title: "Bounce-Monitor — InboxPulse" }] }),
});

function fmt(n: number | null | undefined) {
  if (n == null) return "–";
  return `${n.toFixed(2)} %`;
}

function BounceMonitorPage() {
  const fetchStats = useServerFn(getBounceMonitorStats);
  const [days, setDays] = useState(7);

  const { data, isLoading, error } = useQuery({
    queryKey: ["bounce-monitor-stats", days],
    queryFn: () => fetchStats({ data: { days } }),
    refetchInterval: 30000,
  });

  if (isLoading) return <div className="p-8 text-muted-foreground">Lade…</div>;
  if (error) return <div className="p-8 text-destructive">{(error as Error).message}</div>;
  if (!data) return null;

  const kpis = [
    { label: "Versendet", value: data.sent.toLocaleString() },
    { label: "Bounces gesamt", value: data.totals.all.toLocaleString(), sub: fmt(data.rates.all) },
    { label: "Hard", value: data.totals.hard.toLocaleString(), sub: fmt(data.rates.hard) },
    { label: "Soft", value: data.totals.soft.toLocaleString(), sub: fmt(data.rates.soft) },
    { label: "Block", value: data.totals.block.toLocaleString(), sub: fmt(data.rates.block) },
  ];

  return (
    <div className="p-8 space-y-6">
      <div className="flex items-center justify-between">
        <PageHeader
          title="Bounce-Monitor"
          description="Zentrale Auswertung aller VERP-Bounces über Domains, Kampagnen und Provider."
        />
        <Select value={String(days)} onValueChange={(v) => setDays(Number.parseInt(v, 10))}>
          <SelectTrigger className="w-32">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {[1, 3, 7, 14, 30, 90].map((d) => (
              <SelectItem key={d} value={String(d)}>
                Letzte {d} T.
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <HelpPanel
        title="Hilfe: Bounces aus der Vogelperspektive"
        what={
          <>
            Großes Übersichts-Dashboard: <b>wo</b> bouncen Mails am meisten? Welche Domain, welche
            Kampagne, welcher Anbieter (Gmail, Outlook…)?
          </>
        }
        why={
          <>
            Statt einzelne Bounces zu lesen, siehst du Muster. Beispiel: nur bei Gmail →
            DKIM-Problem. Nur bei einer Domain → Domain hat schlechten Ruf.
          </>
        }
        how={[
          <>
            Oben rechts <b>Zeitraum</b> wählen (1-90 Tage).
          </>,
          <>Kachel-Zahlen oben zeigen Gesamt-Bounces, Rate und Top-Anbieter.</>,
          <>Diagramme unten brechen nach Domain / Kampagne / Provider auf.</>,
          <>
            Bei Auffälligkeit → in <b>Bounce-Log</b> oder <b>Bounces</b> die Details lesen.
          </>,
        ]}
        tips={[
          <>
            Wenn EINE Domain rot wird: Versand von dort sofort pausieren, in <b>Domains</b> DKIM/SPF
            prüfen.
          </>,
        ]}
        glossary={[
          {
            term: "VERP",
            def: "Trick, mit dem wir jeden Bounce eindeutig dem ursprünglichen Versand zuordnen können.",
          },
          {
            term: "Provider",
            def: "Der Mail-Anbieter des Empfängers — Gmail, Outlook, Web.de usw.",
          },
        ]}
      />

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {kpis.map((k) => (
          <Card key={k.label} className="p-4 bg-card/60 border-border">
            <p className="text-xs text-muted-foreground">{k.label}</p>
            <p className="text-2xl font-semibold">{k.value}</p>
            {k.sub && <p className="text-xs text-muted-foreground">{k.sub}</p>}
          </Card>
        ))}
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <Card className="p-4 bg-card/60 border-border">
          <h3 className="font-semibold mb-3">Top Domains</h3>
          <div className="space-y-1 text-sm">
            {data.byDomain.map((d) => (
              <div key={d.domain} className="flex justify-between border-b border-border py-1">
                <span className="truncate">{d.domain}</span>
                <span className="text-xs text-muted-foreground">
                  {d.total} <span className="text-destructive">·{d.hard}H</span> ·{d.soft}S ·
                  {d.block}B
                </span>
              </div>
            ))}
            {data.byDomain.length === 0 && <p className="text-muted-foreground text-xs">Keine.</p>}
          </div>
        </Card>

        <Card className="p-4 bg-card/60 border-border">
          <h3 className="font-semibold mb-3">Top Kampagnen</h3>
          <div className="space-y-1 text-sm">
            {data.byCampaign.map((c) => (
              <div key={c.name} className="flex justify-between border-b border-border py-1">
                <span className="truncate">{c.name}</span>
                <span className="text-xs text-muted-foreground">
                  {c.total} ·{c.hard}H ·{c.soft}S ·{c.block}B
                </span>
              </div>
            ))}
            {data.byCampaign.length === 0 && (
              <p className="text-muted-foreground text-xs">Keine.</p>
            )}
          </div>
        </Card>

        <Card className="p-4 bg-card/60 border-border">
          <h3 className="font-semibold mb-3">Provider</h3>
          <div className="space-y-1 text-sm">
            {data.byProvider.map((p) => (
              <div key={p.provider} className="flex justify-between border-b border-border py-1">
                <span>{p.provider}</span>
                <span className="text-xs text-muted-foreground">{p.count}</span>
              </div>
            ))}
            {data.byProvider.length === 0 && (
              <p className="text-muted-foreground text-xs">Keine.</p>
            )}
          </div>
        </Card>

        <Card className="p-4 bg-card/60 border-border">
          <h3 className="font-semibold mb-3">SMTP-Codes (Top 10)</h3>
          <div className="space-y-1 text-sm">
            {data.byCode.map((c) => (
              <div key={c.code} className="flex justify-between border-b border-border py-1">
                <span className="font-mono">{c.code}</span>
                <span className="text-xs text-muted-foreground">{c.count}</span>
              </div>
            ))}
            {data.byCode.length === 0 && <p className="text-muted-foreground text-xs">Keine.</p>}
          </div>
        </Card>
      </div>

      <Card className="p-4 bg-card/60 border-border">
        <h3 className="font-semibold mb-3">Letzte 50 Bounces</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="text-muted-foreground text-left">
              <tr>
                <th className="py-2 pr-3">Zeit</th>
                <th className="py-2 pr-3">Typ</th>
                <th className="py-2 pr-3">Empfänger</th>
                <th className="py-2 pr-3">Domain</th>
                <th className="py-2 pr-3">Provider</th>
                <th className="py-2 pr-3">Code</th>
                <th className="py-2 pr-3">Match</th>
                <th className="py-2 pr-3">Grund</th>
              </tr>
            </thead>
            <tbody>
              {data.recent.map((b) => (
                <tr key={b.id} className="border-t border-border">
                  <td className="py-1 pr-3 whitespace-nowrap">
                    {new Date(b.received_at).toLocaleString()}
                  </td>
                  <td className="py-1 pr-3">
                    <Badge
                      variant={
                        b.bounce_type === "hard"
                          ? "destructive"
                          : b.bounce_type === "block"
                            ? "destructive"
                            : "secondary"
                      }
                    >
                      {b.bounce_type}
                    </Badge>
                  </td>
                  <td className="py-1 pr-3 truncate max-w-[200px]">{b.recipient_email}</td>
                  <td className="py-1 pr-3">{b.from_domain ?? "–"}</td>
                  <td className="py-1 pr-3">{b.provider ?? "–"}</td>
                  <td className="py-1 pr-3 font-mono">{b.status_code ?? "–"}</td>
                  <td className="py-1 pr-3">{b.verp_match_method ?? "–"}</td>
                  <td className="py-1 pr-3 truncate max-w-[260px]">{b.bounce_reason ?? "–"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
